import * as React from 'react';
import Image from 'antd/lib/image';
import Space from 'antd/lib/space';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import { useSelector } from 'react-redux';

import { releaseStepDetailsContainerModuleStyle, antGrey, titleStyle } from '../styles/globalStyles';
import { BetaFeedbackItem } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone } from '@/selectors/selectors';
import { formatDateWithTimezone } from '@/timeHelpers';

interface BetaFeedbackModuleProps {
    appId: string;
    feedback: BetaFeedbackItem[];
    focusedFeedbackId?: string;
}

const FEEDBACK_MAX_HEIGHT = 300;
const FEEDBACK_FADE_MASK = 'linear-gradient(to bottom, black calc(100% - 48px), transparent 100%)';

const BetaFeedbackModule: React.FC<BetaFeedbackModuleProps> = ({
    appId,
    feedback = [],
    focusedFeedbackId,
}: BetaFeedbackModuleProps): JSX.Element => {
    const titleTextStyle: React.CSSProperties = {
        ...titleStyle,
        color: antGrey.grey7,
        fontSize: 12,
    };

    const scrollRef = React.useRef<HTMLDivElement>(null);
    const focusedFeedbackRef = React.useRef<HTMLDivElement>(null);
    const [showFade, setShowFade] = React.useState(false);
    const updateFade = React.useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        setShowFade(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    }, []);

    React.useEffect(updateFade, [updateFade, feedback.length]);

    React.useEffect(() => {
        if (!focusedFeedbackId) {
            return undefined;
        }

        const frame = requestAnimationFrame(() => focusedFeedbackRef.current?.scrollIntoView({ block: 'center' }));
        return () => cancelAnimationFrame(frame);
    }, [focusedFeedbackId, feedback]);

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    return (
        <div style={{ ...releaseStepDetailsContainerModuleStyle, backgroundColor: antGrey.grey2, overflow: 'hidden' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text style={{ ...titleTextStyle }}>
                    {feedback.length ? `Tester feedback (${feedback.length})` : 'Tester feedback'}
                </Text>
                {feedback.length === 0 ?
                    <Text type="secondary">No tester feedback</Text>
                :   <div
                        ref={scrollRef}
                        onScroll={updateFade}
                        style={{
                            maxHeight: FEEDBACK_MAX_HEIGHT,
                            overflowY: 'auto',
                            paddingRight: 8,
                            maskImage: showFade ? FEEDBACK_FADE_MASK : undefined,
                            WebkitMaskImage: showFade ? FEEDBACK_FADE_MASK : undefined,
                        }}
                    >
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            {feedback.map((item) => {
                                const screenshotUrls = item.screenshotUrls || [];
                                return (
                                    <div
                                        ref={item.id === focusedFeedbackId ? focusedFeedbackRef : undefined}
                                        key={item.id}
                                    >
                                        <Space direction="vertical" size={1} style={{ width: '100%' }}>
                                            <Paragraph ellipsis={{ rows: 2, expandable: true }} style={{ margin: 0 }}>
                                                {item.comment}
                                            </Paragraph>
                                            {screenshotUrls.length ?
                                                <Image.PreviewGroup>
                                                    <Space size={8} wrap>
                                                        {screenshotUrls.map((url, index) => (
                                                            <Image key={index} src={url} height={80} />
                                                        ))}
                                                    </Space>
                                                </Image.PreviewGroup>
                                            :   null}
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {item.testerName || item.testerEmail ?
                                                    <>
                                                        <Text strong type="secondary" style={{ fontSize: 12 }}>
                                                            {item.testerName || item.testerEmail}
                                                        </Text>
                                                        {' · '}
                                                    </>
                                                :   null}
                                                {formatDateWithTimezone(
                                                    item.submittedAt,
                                                    'mobile',
                                                    effectiveTimezone,
                                                    use24HourTimeFormat,
                                                )}
                                            </Text>
                                        </Space>
                                    </div>
                                );
                            })}
                        </Space>
                    </div>
                }
            </Space>
        </div>
    );
};

export default BetaFeedbackModule;
